import React, { useEffect, useMemo, useState } from 'react';
import { Button, Divider, Modal, Space, Switch, Table, Tag, Typography } from 'antd';
import { EyeInvisibleOutlined } from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchServiceVisibilityConfig,
    updateServiceVisibilityItems,
    updateServiceVisibilityToggle,
} from '@/redux/actions/configActions';
import { fetchActiveServices } from '@/redux/actions/serviceActions';
import { checkPermission } from '@/utils/permission';

const flattenTree = (items = []) => items.reduce((result, item) => (
    result.concat(item, flattenTree(item.children || []))
), []);

const buildDisplayTree = (items = [], draft = {}, inheritedHidden = false) => items.map(item => {
    const directHidden = Object.prototype.hasOwnProperty.call(draft, item.id)
        ? draft[item.id]
        : !!item.hide_when_filter_enabled;
    const effectiveHidden = inheritedHidden || directHidden;
    return {
        ...item,
        directHidden,
        effectiveHidden,
        hiddenByAncestor: inheritedHidden && !directHidden,
        children: buildDisplayTree(item.children || [], draft, effectiveHidden),
    };
});

const ServiceVisibilitySettings = () => {
    const dispatch = useDispatch();
    const config = useSelector(state => state.config.serviceVisibility);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState({});
    const canUpdate = checkPermission('service_visibility', 'update');

    useEffect(() => {
        setLoading(true);
        dispatch(fetchServiceVisibilityConfig()).finally(() => setLoading(false));
    }, [dispatch]);

    const openModal = () => {
        const nextDraft = {};
        flattenTree(config?.items || []).forEach(item => {
            nextDraft[item.id] = !!item.hide_when_filter_enabled;
        });
        setDraft(nextDraft);
        setVisible(true);
    };

    const displayTree = useMemo(
        () => buildDisplayTree(config?.items || [], draft),
        [config, draft]
    );

    const save = async () => {
        const changedItems = flattenTree(config?.items || []).filter(item => (
            !!item.hide_when_filter_enabled !== !!draft[item.id]
        )).map(item => ({ service_id: item.id, hidden: !!draft[item.id] }));
        if (!changedItems.length) {
            setVisible(false);
            return;
        }
        setSaving(true);
        try {
            await dispatch(updateServiceVisibilityItems(changedItems, config.version));
            await dispatch(fetchServiceVisibilityConfig());
            await dispatch(fetchActiveServices());
            setVisible(false);
        } finally {
            setSaving(false);
        }
    };

    const changeMasterToggle = async checked => {
        setLoading(true);
        try {
            await dispatch(updateServiceVisibilityToggle(checked, config.version));
            await dispatch(fetchServiceVisibilityConfig());
            await dispatch(fetchActiveServices());
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Danh mục dịch vụ',
            dataIndex: 'name',
            key: 'name',
            render: (name, item) => <Space><span>{name}</span>{item.code ? <Typography.Text type="secondary">({item.code})</Typography.Text> : null}</Space>,
        },
        {
            title: 'Trạng thái hiệu lực',
            key: 'effective',
            width: '28%',
            render: (_, item) => item.effectiveHidden
                ? <Tag color={item.hiddenByAncestor ? 'orange' : 'red'}>{item.hiddenByAncestor ? 'Ẩn theo danh mục cha' : 'Ẩn trực tiếp'}</Tag>
                : <Tag color="green">Hiển thị</Tag>,
        },
        {
            title: 'Ẩn trực tiếp',
            key: 'hidden',
            width: '18%',
            align: 'center',
            render: (_, item) => (
                <Switch
                    checked={item.directHidden}
                    disabled={!canUpdate}
                    onChange={checked => setDraft(current => ({ ...current, [item.id]: checked }))}
                />
            ),
        },
    ];

    return (
        <div>
            <Divider orientation="left">Ẩn dữ liệu theo danh mục dịch vụ</Divider>
            <Space direction="vertical" size="middle">
                <Space>
                    <span>Áp dụng cấu hình ẩn dịch vụ</span>
                    <Switch
                        checked={!!config?.enabled}
                        loading={loading}
                        disabled={!canUpdate || !config}
                        onChange={changeMasterToggle}
                    />
                </Space>
                <Typography.Text type="secondary">
                    Khi bật, danh mục được ẩn trực tiếp và toàn bộ danh mục con của nó sẽ không xuất hiện trong các màn hình nghiệp vụ. Tắt để hiển thị toàn bộ dữ liệu mà không làm mất các danh mục đã cấu hình ẩn.
                </Typography.Text>
                <Button icon={<EyeInvisibleOutlined />} onClick={openModal} loading={loading} disabled={!config}>
                    Cấu hình ẩn dịch vụ
                </Button>
            </Space>
            <Modal
                title="Cấu hình ẩn dịch vụ"
                visible={visible}
                width={isMobile ? '100%' : '78%'}
                maskClosable={false}
                destroyOnClose
                onCancel={() => setVisible(false)}
                onOk={save}
                okText="Lưu cấu hình"
                cancelText="Đóng"
                confirmLoading={saving}
                okButtonProps={{ disabled: !canUpdate }}
            >
                <Typography.Paragraph type="secondary">
                    Công tắc chỉ lưu trạng thái của chính danh mục. Danh mục con có thể bị ẩn theo cha nhưng trạng thái trực tiếp của nó vẫn được giữ nguyên.
                </Typography.Paragraph>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={displayTree}
                    pagination={false}
                    loading={loading}
                    scroll={{ x: 'max-content', y: '55vh' }}
                    defaultExpandAllRows
                />
            </Modal>
        </div>
    );
};

export default ServiceVisibilitySettings;
